import type { OgsmBoard, OgsmBoardNode } from '@/types/api'

export interface OgsmMove {
  nodeId: string
  /** Final zero-based index among the node's siblings. */
  position: number
}

/**
 * The sibling list holding `nodeId` (objectives, or a parent's strategies).
 * Walks the whole tree because the backend nests to its own configured depth.
 */
function siblingsOf(
  nodes: OgsmBoardNode[],
  nodeId: string
): OgsmBoardNode[] | null {
  if (nodes.some((node) => node.id === nodeId)) return nodes
  for (const node of nodes) {
    const found = siblingsOf(node.strategies, nodeId)
    if (found) return found
  }
  return null
}

/** The node with this id anywhere in the board, or null. */
export function findNode(
  board: OgsmBoard,
  nodeId: string
): OgsmBoardNode | null {
  return (
    siblingsOf(board.objectives, nodeId)?.find((node) => node.id === nodeId) ??
    null
  )
}

/** The ids of the node's sibling list (objectives, or its parent's strategies), the node included, in order; [] if not found. */
export function siblingIds(board: OgsmBoard, nodeId: string): string[] {
  return siblingsOf(board.objectives, nodeId)?.map((node) => node.id) ?? []
}

/** 0-based index and list size of the node among its siblings, or null if not found. */
export function positionOf(
  board: OgsmBoard,
  nodeId: string
): { index: number; total: number } | null {
  const ids = siblingIds(board, nodeId)
  const index = ids.indexOf(nodeId)
  return index === -1 ? null : { index, total: ids.length }
}

/** The move a drop of `activeId` on `overId` asks for, or null when they are the same node or not siblings. */
export function planMove(
  board: OgsmBoard,
  activeId: string,
  overId: string
): OgsmMove | null {
  if (activeId === overId) return null
  const position = siblingIds(board, activeId).indexOf(overId)
  return position === -1 ? null : { nodeId: activeId, position }
}

function moveWithin(
  nodes: OgsmBoardNode[],
  { nodeId, position }: OgsmMove
): OgsmBoardNode[] | null {
  const from = nodes.findIndex((node) => node.id === nodeId)
  if (from !== -1) {
    const rest = nodes.filter((node) => node.id !== nodeId)
    const to = Math.min(Math.max(position, 0), rest.length)
    rest.splice(to, 0, nodes[from])
    return rest.map((node, index) =>
      node.sortOrder === index ? node : { ...node, sortOrder: index }
    )
  }
  for (const [index, node] of nodes.entries()) {
    const strategies = moveWithin(node.strategies, { nodeId, position })
    if (strategies) {
      const next = [...nodes]
      next[index] = { ...node, strategies }
      return next
    }
  }
  return null
}

/** The board with the node moved to `position` in its own sibling list (clamped to the list), at any depth.
 *  Renumbers `sortOrder` of that list to its new index. Returns `board` unchanged if the node is not found. */
export function applyMove(board: OgsmBoard, move: OgsmMove): OgsmBoard {
  const objectives = moveWithin(board.objectives, move)
  return objectives ? { ...board, objectives } : board
}
